import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import ClientDepositForm from './ClientDepositForm';
import RequestPayoutModal from './RequestPayoutModal';
import LoadingPopup from '../../SharedView/LoadingPopup';
import usePaymentReturn from '../usePaymentReturn';
import PaymentResultOverlay from '../PaymentResultOverlay';
import {
    isPaymentInReview,
    paymentInReviewMessage,
    isPendingDrift,
    driftCaptureTotal,
    pendingDriftMessage,
    paymentErrorMessage,
    readCancelSettlement,
    readCancelErrorSettlement,
    CANCEL_UNCONFIRMED_MESSAGE,
    AWAITING_PAYMENT_NOTE,
    PAYMENT_IN_REVIEW_STATE,
    REVIEW_HELD_NOTE,
    REVIEW_HELD_OTHER_BOOKINGS_NOTE,
} from '../paymentErrors';
import useBfcacheReset from '../useBfcacheReset';
import { availableBalance, amountOwed, amountOwedCount, isInDebt } from '../walletFigures';
import './clientWallet.css';

// B2 (2026-07-19) — the transactions page is sized so header + table + pager
// fit a standard desktop viewport. 8 compact rows with 2-line-clamped
// descriptions keep the table inside ~1280×800 without vertical scrolling;
// history stays reachable through the pager.
const TX_PAGE_SIZE = 8;

// Reference-number chip — compact monospace pill for the TOP-<id> reference.
const REF_CHIP = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
    color: '#475569', background: '#eef2f7',
    padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
};

export default function ClientWallet() {
    const navigate = useNavigate();
    const [wallet, setWallet] = useState(null);
    const [walletLoading, setWalletLoading] = useState(true);

    const [tab, setTab] = useState('transactions');

    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsTotal, setTransactionsTotal] = useState(0);

    const [deposits, setDeposits] = useState([]);
    const [depositsLoading, setDepositsLoading] = useState(false);

    const [showDepositForm, setShowDepositForm] = useState(false);

    // Payouts (wallet cash → manual bank transfer)
    const [payouts, setPayouts] = useState([]);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [cancellingPayoutId, setCancellingPayoutId] = useState(null);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const dt = DateTime.fromISO(dateString, { zone: 'Asia/Dubai' });
        return dt.toFormat('dd MMM yy, HH:mm');
    };

    // Separate wallets per billing entity (PLAN_entity_wallets.md): the
    // client's wallets — almost always just one. `walletId` scopes every read
    // and top-up below; null = the main wallet.
    const [wallets, setWallets] = useState([]);
    const [walletId, setWalletId] = useState(null);
    const walletParams = useMemo(() => (walletId ? { wallet: walletId } : {}), [walletId]);
    useEffect(() => {
        axios.get('/api/client-requests/wallet/wallets')
            .then((res) => setWallets(res.data?.wallets || []))
            .catch(() => setWallets([]));
    }, []);
    const currentWallet = wallets.find((w) => w.id === walletId) || wallets.find((w) => w.is_main) || null;

    const fetchWallet = useCallback(async () => {
        try {
            setWalletLoading(true);
            const res = await axios.get('/api/client-requests/wallet', { params: walletParams });
            setWallet(res.data);
        } catch (err) {
            console.error('Error fetching wallet:', err);
        } finally {
            setWalletLoading(false);
        }
    }, [walletParams]);

    const fetchTransactions = useCallback(async (page = 1) => {
        try {
            setTransactionsLoading(true);
            const res = await axios.get(`/api/client-requests/wallet/transactions?page=${page}&limit=${TX_PAGE_SIZE}`, { params: walletParams });
            setTransactions(res.data.transactions);
            setTransactionsTotal(res.data.total);
            setTransactionsPage(page);
        } catch (err) {
            console.error('Error fetching transactions:', err);
        } finally {
            setTransactionsLoading(false);
        }
    }, [walletParams]);

    const fetchDeposits = useCallback(async () => {
        try {
            setDepositsLoading(true);
            const res = await axios.get('/api/client-requests/wallet/deposits', { params: walletParams });
            setDeposits(res.data);
        } catch (err) {
            console.error('Error fetching deposits:', err);
        } finally {
            setDepositsLoading(false);
        }
    }, [walletParams]);

    const fetchPayouts = useCallback(async () => {
        try {
            const res = await axios.get('/api/payouts', { params: walletParams });
            setPayouts(Array.isArray(res.data) ? res.data : (res.data?.payouts || []));
        } catch (err) {
            // Payouts may not be deployed yet — the page must not break without them.
            if (err.response?.status !== 404) console.error('Error fetching payouts:', err);
            setPayouts([]);
        }
    }, [walletParams]);

    useEffect(() => {
        fetchWallet();
        fetchTransactions(1);
        fetchDeposits();
        fetchPayouts();
    }, [fetchWallet, fetchTransactions, fetchDeposits, fetchPayouts]);

    // Back from the hosted card paypage (?payment=return&ref=…): verify the
    // order against the gateway (poll while pending) and show the outcome in
    // the full-screen PaymentResultOverlay. On paid we refresh everything and
    // look the credited deposit up by id as an amount FALLBACK (the /verify
    // response normally carries amount + purpose itself).
    const [cardPaidAmount, setCardPaidAmount] = useState(null);
    const payReturn = usePaymentReturn({
        onPaid: async (data) => {
            fetchWallet();
            fetchTransactions(1);
            try {
                const res = await axios.get('/api/client-requests/wallet/deposits', { params: walletParams });
                setDeposits(res.data);
                const dep = (Array.isArray(res.data) ? res.data : []).find(
                    (d) => Number(d.id) === Number(data?.deposit_id)
                );
                if (dep) setCardPaidAmount(dep.amount);
            } catch {
                // Overlay falls back to generic copy without the amount.
            }
        },
    });

    const handleDepositSuccess = () => {
        setShowDepositForm(false);
        fetchWallet();
        fetchTransactions(1);
        fetchDeposits();
    };

    // ---- Unreconciled card payments (checkout still 'created') ----
    // 'created' means ONLY "we opened a paypage and have not reconciled a result
    // yet". It is NOT evidence that the money did not move: the server's own
    // cancel path treats a 'created' row as possibly holding AUTHORISED /
    // PURCHASED / CAPTURED / POST_AUTH_REFER / REVIEW money ('money_in_flight',
    // routes/payments.js:864-872). A completed payment whose redirect was lost
    // or whose webhook lagged sits here too. So this state is rendered as
    // actionable-and-UNCONFIRMED (complete / cancel) — never as an assertion
    // that nothing was charged. The `checkout_status === 'created'` arm below is
    // deliberately kept: it only decides WHICH row gets the unconfirmed
    // treatment, and dropping it would silently downgrade such a row to a plain
    // "processing" deposit with no way for the client to resolve it.
    //
    // EXCEPTION: a review-held checkout is also `status === 'created'`, but the
    // bank is holding an authorised payment and the stored paypage session has
    // already been consumed. Offering "Complete payment" there is a dead button
    // AND an invitation to pay twice, so those rows get the calm review
    // treatment instead of the resume/cancel pair.
    const isReviewHeld = (d) => d.payment_state === PAYMENT_IN_REVIEW_STATE;
    const isAwaitingPayment = (d) => !isReviewHeld(d)
        && (d.payment_state === 'awaiting_payment' || d.checkout_status === 'created');
    const awaitingDeposits = deposits.filter((d) => d.status === 'pending' && isAwaitingPayment(d));
    const reviewHeldDeposits = deposits.filter((d) => d.status === 'pending' && isReviewHeld(d));
    const [checkoutActionId, setCheckoutActionId] = useState(null);
    // Persistent money notice for one awaiting-payment row (amount changed, the
    // gateway is still reviewing, or it turned out already paid). Not an error.
    const [checkoutNotice, setCheckoutNotice] = useState(null); // { id, text }
    // Fresh figures quoted by PENDING_DRIFT and shown to the client, per row —
    // resent as expected_total so the next attempt pays an acknowledged amount.
    const [checkoutAckTotals, setCheckoutAckTotals] = useState({});

    // Restored from the paypage's Back button (bfcache) → clear any frozen
    // in-flight state so the resume/cancel buttons work again.
    useBfcacheReset(() => setCheckoutActionId(null));

    const refreshAll = useCallback(() => {
        fetchWallet();
        fetchTransactions(1);
        fetchDeposits();
    }, [fetchWallet, fetchTransactions, fetchDeposits]);

    const handleCompleteCheckout = async (d) => {
        if (checkoutActionId) return;
        const ackTotal = checkoutAckTotals[d.id];
        // ALWAYS re-stake through /retry — the stored paypage URL points at an
        // N-Genius session that expires server-side, so reopening it later
        // lands on a dead gateway page (2026-08-06).
        setCheckoutActionId(d.id);
        setCheckoutNotice(null);
        try {
            const body = { order_ref: d.order_ref };
            if (ackTotal != null) body.expected_total = ackTotal;
            const res = await axios.post('/api/payments/retry', body);
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // navigating away
            }
            window.alert('Could not open the payment page. Please refresh and try again.');
        } catch (err) {
            // The amount due moved: quote the fresh figure and remember it so the
            // next tap pays the acknowledged amount instead of dead-ending.
            if (isPendingDrift(err)) {
                const fresh = driftCaptureTotal(err);
                if (fresh != null) setCheckoutAckTotals((prev) => ({ ...prev, [d.id]: fresh }));
                setCheckoutNotice({ id: d.id, text: pendingDriftMessage(err) });
            } else if (isPaymentInReview(err)) {
                setCheckoutNotice({ id: d.id, text: paymentInReviewMessage(err) });
            } else {
                window.alert(paymentErrorMessage(err, 'Could not open the payment page. Please try again.'));
            }
        } finally {
            setCheckoutActionId(null);
        }
    };

    const handleCancelCheckout = async (d) => {
        if (checkoutActionId) return;
        // Conditional by design: whether anything was charged is only known once
        // the server has checked the gateway — it may come back paid or in review.
        if (!window.confirm(`Cancel this card payment of ${money(d.amount)}? If it hasn’t been completed, nothing will be charged.`)) return;
        setCheckoutActionId(d.id);
        setCheckoutNotice(null);
        try {
            const res = await axios.post('/api/payments/cancel', { order_ref: d.order_ref });
            // ONE reader decides (shared with mobile). 'paid' = the payment had
            // in fact completed (lost redirect) and this call settled it. The
            // wallet WAS credited — never imply otherwise. Anything that is not
            // an EXPLICIT not-paid outcome is 'unconfirmed', so the row can
            // never quietly vanish as though nothing happened.
            const settlement = readCancelSettlement(res);
            if (settlement === 'paid') {
                setCheckoutNotice({
                    id: d.id,
                    text: 'This payment had already gone through, so it could not be cancelled — '
                        + 'your payment was received and your wallet has been credited.',
                });
            } else if (settlement !== 'cancelled') {
                setCheckoutNotice({ id: d.id, text: CANCEL_UNCONFIRMED_MESSAGE });
            }
            refreshAll();
        } catch (err) {
            const settlement = readCancelErrorSettlement(err);
            if (settlement === 'review') {
                setCheckoutNotice({ id: d.id, text: paymentInReviewMessage(err) });
            } else if (settlement === 'paid') {
                setCheckoutNotice({
                    id: d.id,
                    text: 'This payment had already gone through, so it could not be cancelled — '
                        + 'your payment was received and your wallet has been credited.',
                });
            } else if (settlement === 'unconfirmed' && err.response) {
                // Bare 409/503/5xx about this checkout: ambiguous about money.
                // Persistent "still confirming" — never "nothing was charged".
                setCheckoutNotice({ id: d.id, text: CANCEL_UNCONFIRMED_MESSAGE });
            } else if (settlement === 'unconfirmed') {
                window.alert(paymentErrorMessage(err, 'Could not cancel this payment. Please refresh and try again.'));
            }
            refreshAll();
        } finally {
            setCheckoutActionId(null);
        }
    };

    // ---- Payouts (cash only — bonus is never withdrawable) ----
    // Pending payouts reserve their amount: withdrawable = cash − pending payouts.
    // Prefer server-computed figures from the wallet summary when present.
    const pendingPayouts = payouts.filter((p) => p.status === 'requested');
    const pendingPayoutsTotal = wallet?.pending_payouts_total != null
        ? Number(wallet.pending_payouts_total)
        : pendingPayouts.reduce((s, p) => s + Number(p.amount || 0), 0);
    const withdrawable = wallet?.withdrawable_balance != null
        ? Math.max(0, Number(wallet.withdrawable_balance))
        : Math.max(0, Math.round((Number(wallet?.cash_balance || 0) - pendingPayoutsTotal) * 100) / 100);

    // Primary-card figures (2026-07-15 header redesign). Available to
    // spend = server-computed available_balance (total − reserved − pending
    // payouts; may be negative when over-committed).
    // Read through the shared decider so the wallet page's headline can never
    // drift from ClientHome's / the checkout summaries'.
    const availableToSpend = wallet ? availableBalance(wallet) : 0;
    // Only surface the withdrawable line when it differs from plain cash
    // (i.e. reservations / pending payouts / bonus make "cash" misleading).
    const withdrawableDiffers = Math.abs(withdrawable - Number(wallet?.cash_balance || 0)) > 0.005;

    const handlePayoutSuccess = () => {
        setShowPayoutModal(false);
        fetchWallet();
        fetchPayouts();
    };

    const handleCancelPayout = async (payout) => {
        if (cancellingPayoutId) return;
        if (!window.confirm(`Cancel the pending payout of ${money(payout.amount)}? The amount becomes spendable again.`)) return;
        setCancellingPayoutId(payout.id);
        try {
            await axios.post(`/api/payouts/${payout.id}/cancel`);
            await Promise.all([fetchWallet(), fetchPayouts()]);
        } catch (err) {
            console.error('Error cancelling payout:', err);
            window.alert(err.response?.data?.error || 'Could not cancel this payout. Please refresh and try again.');
            await fetchPayouts();
        } finally {
            setCancellingPayoutId(null);
        }
    };

    const typeLabel = (t) => {
        if (t.type === 'booking_charge') {
            // Check if description has change details (Type:, Added:, etc.)
            const desc = t.description || '';
            return (desc.includes('Type:') || desc.includes('Added:') || desc.includes('Removed:') || desc.includes('Amenities') || desc.includes('Time changed'))
                ? 'Booking Change' : 'Booking';
        }
        if (t.type === 'booking_refund') return 'Refund';
        // P1 — A CARD PAYMENT IS NOT A TOP-UP.
        //
        // Every deposit row that reaches this list is a GENUINE wallet top-up:
        // card booking payments are first-class receipts and the backend keeps
        // them out of the wallet feeds entirely. The old "Card Payment" label
        // was therefore the exact confusion the owner objected to — it made a
        // wallet top-up read like a booking payment, and (in the other
        // direction) trained clients to look for their booking payments here.
        // The label now names what the row IS — a top-up — and only says HOW it
        // was funded. Booking card payments live on the booking.
        if (t.type === 'deposit') {
            if (t.source_payment_method === 'card') return 'Wallet Top-up (card)';
            if (t.source_payment_method === 'bank_transfer') return 'Wallet Top-up (bank transfer)';
            return 'Wallet Top-up';
        }
        if (t.type === 'bonus_credit') return 'Bonus Credit';
        if (t.type === 'card_refund') return 'Card Refund';
        if (t.type === 'adjustment') {
            // 'adjustment' is the storage type for several distinct events (they
            // all flow through adjustBalance). Name what the row IS for the
            // client instead of the generic "Adjustment": a pending-invoice
            // settlement is a payment out of the wallet; a settlement refund is
            // money back. (2026-07-21)
            const desc = (t.description || '').trim();
            if (/^settled pending invoices/i.test(desc)) return 'Invoice Payment';
            if (/^refund\b/i.test(desc)) return 'Refund';
            return 'Adjustment';
        }
        if (t.type === 'payout') return 'Payout';
        if (t.type === 'adjustment_charge' || t.type === 'charge_adjustment') return 'Price Adjustment';
        return t.type;
    };

    // A description that is empty or nothing but an amount ("250", "4,346.65",
    // "AED 250.00", "-250"). Manual balance adjustments carry FREEFORM admin
    // text and occasionally hold only the amount, which is meaningless on a
    // client ledger — those fall back to a clean label. (2026-07-21)
    const isBareAmountDescription = (desc) => {
        const stripped = String(desc || '')
            .replace(/aed/gi, '')
            .replace(/[,\s]/g, '')
            .replace(/^[+-]/, '')
            .trim();
        return stripped === '' || /^\d+(\.\d+)?$/.test(stripped);
    };

    const formatDescription = (t) => {
        const desc = (t.description || '').trim();
        // Junk/empty manual-adjustment text → clean label. The amount and its
        // sign already live in the Amount column, so "Balance adjustment" reads
        // clearly next to e.g. "−AED 250.00". Only adjustments (freeform admin
        // text); every other type has a system-generated description.
        if (t.type === 'adjustment' && isBareAmountDescription(desc)) {
            return 'Balance adjustment';
        }
        // Replace "Booking #123: ..." with "#123: ..."
        let cleaned = desc.replace(/^Booking #(\d+)/, '#$1');
        // Remove the (old → new) amount part
        cleaned = cleaned.replace(/\s*\(\d+\.\d+\s*→\s*\d+\.\d+\)/, '');
        // Replace "price increase" / "price decrease" with cleaner text
        cleaned = cleaned.replace(/price increase/gi, 'Price Increase');
        cleaned = cleaned.replace(/price decrease/gi, 'Price Decrease');
        cleaned = cleaned.replace(/price change/gi, 'Price Change');
        return cleaned.trim();
    };

    const typeChipClass = (type) => {
        switch (type) {
            case 'deposit':
            case 'bonus_credit':
            case 'booking_refund':
                return 'cwallet__chip--green';
            case 'booking_charge':
            case 'payout':
            case 'adjustment_charge':
            case 'charge_adjustment':
                return 'cwallet__chip--red';
            case 'adjustment':
                return 'cwallet__chip--orange';
            default:
                return '';
        }
    };

    const depositStatusChip = (status) => {
        switch (status) {
            case 'approved': return 'cwallet__chip--green';
            case 'rejected': return 'cwallet__chip--red';
            case 'pending': return 'cwallet__chip--orange';
            default: return '';
        }
    };

    const totalPages = Math.ceil(transactionsTotal / TX_PAGE_SIZE);

    // Full-page loader only on the FIRST load. Refetches (e.g. the payment-
    // return overlay's on-paid refresh) keep the page mounted so the overlay
    // isn't blanked out by a spinner mid-result — the figures update in place.
    if (walletLoading && wallet === null) {
        return <LoadingPopup />;
    }

    return (
        <div className="cwallet">
            {/* Header — the payout entry point lives inside the primary balance
                card next to the withdrawable line (2026-07-15 redesign). */}
            <div className="cwallet__header">
                <h1 className="cwallet__title">{currentWallet && !currentWallet.is_main ? `${currentWallet.entity_label} wallet` : 'Wallet'}</h1>
            </div>

            {/* Separate wallets per billing entity — one chip per wallet, only
                when there is more than one. */}
            {wallets.length > 1 && (
                <div className="cwallet__wallets" role="tablist" aria-label="Wallets">
                    {wallets.map((w) => (
                        <button
                            type="button"
                            role="tab"
                            key={w.id}
                            aria-selected={(walletId || wallets.find((x) => x.is_main)?.id) === w.id}
                            className={`cwallet__walletChip${(walletId || wallets.find((x) => x.is_main)?.id) === w.id ? ' is-active' : ''}`}
                            onClick={() => setWalletId(w.is_main ? null : w.id)}
                        >
                            <span className="cwallet__walletChipLabel">{w.label}</span>
                            <span className="cwallet__walletChipAmount">AED {Number(w.available_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Card payment outcome (verified against the gateway, never the
                redirect) — full-screen result overlay shared by all return
                surfaces. */}
            <PaymentResultOverlay payReturn={payReturn} surface="wallet" fallbackAmount={cardPaidAmount} />

            {/* Balance hero (2026-07-15 redesign): ONE primary
                "Available to spend" card (coral accent, biggest number, gross
                figures in the sub-line, withdrawable + payout entry point when
                relevant) + a slim secondary row of uniform white left-accent
                cards (Reserved / Incoming top-ups / Payout pending — each only
                when nonzero). Tabs stay on the right. */}
            <div className="cwallet__toolbar">
                <div className="cwallet__balances">
                    <div className="cwallet__primaryCard">
                        <div className="cwallet__primaryLabel">Available to spend</div>
                        <div className={`cwallet__primaryValue ${availableToSpend < 0 ? 'cwallet__primaryValue--negative' : ''}`}>
                            {money(availableToSpend)}
                        </div>
                        <div className="cwallet__primarySub">
                            Total {money(wallet?.total_balance)}
                            <span className="cwallet__primarySubSep" aria-hidden="true">·</span>
                            Cash {money(wallet?.cash_balance)}
                            <span className="cwallet__primarySubSep" aria-hidden="true">·</span>
                            Bonus {money(wallet?.bonus_balance)}
                        </div>
                        {/* The long "you are AED X short" sentence was removed
                            (2026-07-19): the committed breakdown renders
                            directly beneath this headline and already names each
                            commitment and its amount, so the paragraph only
                            restated it. overcommittedExplanation() is kept in
                            walletFigures for callers that have no breakdown
                            beside them. */}
                        {withdrawableDiffers && (
                            <div className="cwallet__primaryPayoutRow">
                                <span className="cwallet__primaryWithdrawable">
                                    Withdrawable (cash): {money(withdrawable)}
                                </span>
                            </div>
                        )}
                    </div>

                    {(Number(wallet?.reserved_for_pending) > 0
                        || Number(wallet?.pending_deposits_all_total) > 0
                        || isInDebt(wallet)
                        || pendingPayoutsTotal > 0.005) && (
                        <div className="cwallet__miniCards">
                            {Number(wallet?.reserved_for_pending) > 0 && (
                                <div className="cwallet__miniCard cwallet__miniCard--reserved">
                                    <div className="cwallet__miniLabel">
                                        Reserved · {wallet.pending_bookings_count} pending
                                    </div>
                                    <div className="cwallet__miniValue">−{money(wallet?.reserved_for_pending)}</div>
                                </div>
                            )}
                            {Number(wallet?.pending_deposits_all_total) > 0 && (
                                <div className="cwallet__miniCard cwallet__miniCard--incoming">
                                    <div className="cwallet__miniLabel">Incoming top-ups</div>
                                    <div className="cwallet__miniValue">+{money(wallet?.pending_deposits_all_total)}</div>
                                    {Number(wallet?.pending_deposits_earmarked_total) > 0 && (
                                        <div className="cwallet__miniSub">
                                            {money(wallet?.pending_deposits_earmarked_total)} backs pending bookings
                                        </div>
                                    )}
                                </div>
                            )}
                            {pendingPayoutsTotal > 0.005 && (
                                <div className="cwallet__miniCard cwallet__miniCard--payout">
                                    <div className="cwallet__miniLabel">Payout pending</div>
                                    <div className="cwallet__miniValue">−{money(pendingPayoutsTotal)}</div>
                                </div>
                            )}
                            {/* AMOUNT OWED — its own labelled figure, never netted
                                into the headline above and never described as
                                "set aside" (2026-07-18). Money you owe is not
                                money that stays yours. */}
                            {/* TAPPABLE (2026-07-19). This tile used to be
                                dead text ending in "Settle from My Bookings" —
                                a figure announcing a debt with no way to reach
                                it, while the only lists that showed it were
                                filtered or scoped to one booking at a time.
                                THE INVARIANT: any nonzero "Amount owed" is one
                                tap from an itemised, actionable list. */}
                            {/* Price adjustments retired (2026-07-21) — the
                                "Amount owed → price adjustments" tile is gone. */}
                            {false && isInDebt(wallet) && (
                                <button
                                    type="button"
                                    className="cwallet__miniCard cwallet__miniCard--owed cwallet__miniCard--link"
                                    onClick={() => navigate('/client/adjustments')}
                                    title="View and settle your price adjustments"
                                >
                                    <div className="cwallet__miniLabel">
                                        Amount owed{amountOwedCount(wallet) > 0 ? ` · ${amountOwedCount(wallet)} price adjustment${amountOwedCount(wallet) === 1 ? '' : 's'}` : ''}
                                    </div>
                                    <div className="cwallet__miniValue">{money(amountOwed(wallet))}</div>
                                    <div className="cwallet__miniSub">View &amp; settle ›</div>
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions + tab toggle share the right column of the balance row (the owner). */}
                <div className="cwallet__toolbarRight">
                    <div className="cwallet__headerBtns">
                        <button className="cwallet__addFundsBtn" onClick={() => setShowDepositForm(true)}>
                            + Add Funds
                        </button>
                        {withdrawable > 0.005 && (
                            <button
                                type="button"
                                className="cwallet__payoutBtn cwallet__payoutBtn--header"
                                onClick={() => setShowPayoutModal(true)}
                            >
                                Withdraw cash balance
                            </button>
                        )}
                    </div>
                    <div className="cwallet__tabs">
                        <button
                            className={`cwallet__tabBtn ${tab === 'transactions' ? 'is-active' : ''}`}
                            onClick={() => setTab('transactions')}
                        >
                            Transactions
                        </button>
                        <button
                            className={`cwallet__tabBtn ${tab === 'deposits' ? 'is-active' : ''}`}
                            onClick={() => setTab('deposits')}
                        >
                            Top-ups
                        </button>
                    </div>
                </div>
            </div>

            {/* Pending payouts — reserved cash awaiting the owner's manual bank transfer */}
            {pendingPayouts.map((p) => (
                <div className="cwallet__payoutPending" key={`payout-${p.id}`}>
                    <div className="cwallet__payoutPendingMain">
                        <div className="cwallet__payoutPendingTop">
                            <span className="cwallet__chip cwallet__chip--orange">Payout requested</span>
                            <span className="cwallet__payoutPendingAmount">{money(p.amount)}</span>
                        </div>
                        <div className="cwallet__payoutPendingMeta">
                            To {p.account_name || '—'}
                            {p.iban ? ` · IBAN …${String(p.iban).slice(-4)}` : ''}
                            {p.bank_name ? ` · ${p.bank_name}` : ''}
                            {' · Requested '}{formatDate(p.requested_at || p.created_at)}
                        </div>
                        <div className="cwallet__payoutPendingNote">
                            Processed manually by bank transfer — this amount is set aside and not spendable while pending.
                        </div>
                    </div>
                    <button
                        type="button"
                        className="cwallet__payoutCancelBtn"
                        onClick={() => handleCancelPayout(p)}
                        disabled={cancellingPayoutId === p.id}
                    >
                        {cancellingPayoutId === p.id ? 'Cancelling…' : 'Cancel'}
                    </button>
                </div>
            ))}

            {/* Review-held card payments — the bank is holding the money. No
                actions: nothing for the client to do, and no truthful way to
                offer a cancel. Calm and standing, never amber/red. */}
            {reviewHeldDeposits.map((d) => (
                <div className="cwallet__awaitBanner cwallet__awaitBanner--review" key={`review-${d.id}`}>
                    <div className="cwallet__awaitMain">
                        <div className="cwallet__awaitTop">
                            <span className="cwallet__chip cwallet__chip--review">Payment in review</span>
                            <span className="cwallet__awaitAmount">{money(d.amount)}</span>
                        </div>
                        <div className="cwallet__awaitNote">
                            {REVIEW_HELD_NOTE}
                            <div className="cwallet__awaitNoteStrong">{REVIEW_HELD_OTHER_BOOKINGS_NOTE}</div>
                        </div>
                    </div>
                </div>
            ))}

            {/* Unfinished card payments — a checkout the client opened but never
                completed. Actionable (complete / cancel), never dressed up as
                money being processed. */}
            {awaitingDeposits.map((d) => (
                <div className="cwallet__awaitBanner" key={`await-${d.id}`}>
                    <div className="cwallet__awaitMain">
                        <div className="cwallet__awaitTop">
                            <span className="cwallet__chip cwallet__chip--awaitPay">Awaiting payment</span>
                            <span className="cwallet__awaitAmount">{money(d.amount)}</span>
                        </div>
                        <div className="cwallet__awaitNote">
                            {AWAITING_PAYMENT_NOTE}
                        </div>
                        {/* Persistent money notice for this checkout — the amount
                            changed, it is under gateway review, or already paid. */}
                        {checkoutNotice && checkoutNotice.id === d.id && (
                            <div className="cwallet__awaitNotice">{checkoutNotice.text}</div>
                        )}
                    </div>
                    <div className="cwallet__awaitBtns">
                        <button
                            type="button"
                            className="cwallet__awaitBtn cwallet__awaitBtn--primary"
                            onClick={() => handleCompleteCheckout(d)}
                            disabled={!!checkoutActionId}
                        >
                            {checkoutAckTotals[d.id] != null ? 'Pay updated amount' : 'Complete payment'}
                        </button>
                        <button
                            type="button"
                            className="cwallet__awaitBtn cwallet__awaitBtn--ghost"
                            onClick={() => handleCancelCheckout(d)}
                            disabled={!!checkoutActionId}
                        >
                            {checkoutActionId === d.id ? 'Working…' : 'Cancel'}
                        </button>
                    </div>
                </div>
            ))}

            {/* Empty wallet → nudge the bonus tiers; any balance → unchanged.
                (wallet && …) so the note can't flash while the balance is still loading.

                DELIBERATELY total_balance, not available (G8): this predicate asks
                "does this client have any money with us at all?", to decide whether
                to pitch the top-up bonus tiers. A client whose whole balance is
                reserved against pending requests has money — nudging them to top up
                for a bonus would be wrong. It renders no figure, so the
                headline rule (available only) does not apply. */}
            {wallet && !(Number(wallet.total_balance) > 0) && (
                <div className="cwallet__bonusNote">
                    💡 Top up your wallet and earn bonuses — +5% on 5,000 AED, +10% on 15,000 AED.
                </div>
            )}

            {/* Transactions tab */}
            {tab === 'transactions' && (
                <>
                    {transactionsLoading ? (
                        <div className="cwallet__loading"><LoadingPopup /></div>
                    ) : transactions.length === 0 ? (
                        <div className="cwallet__empty">
                            <div className="cwallet__empty-title">No Transactions Yet</div>
                            <div className="cwallet__empty-text">Your wallet transaction history will appear here.</div>
                        </div>
                    ) : (
                        <>
                            <div className="cwallet__tableWrap">
                            <table className="cwallet__table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Balance After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((t) => {
                                        const isBookingTxn = t.reference_id && (t.type === 'booking_charge' || t.type === 'booking_refund');
                                        const isClickable = isBookingTxn && !t.booking_is_deleted;
                                        // Receipt context from the API (invoice_ids) — only invoice
                                        // references get a chip. A booking transaction needs none: the
                                        // whole row is already clickable to the booking (cursor +
                                        // hover) and the description names it ("#22215 charge").
                                        const invoiceIds = Array.isArray(t.invoice_ids) ? t.invoice_ids : [];
                                        return (
                                        <tr
                                            key={t.id}
                                            className={isClickable ? 'cwallet__rowLink' : ''}
                                            onClick={isClickable ? () => navigate(`/client/bookings/${t.reference_id}`) : undefined}
                                        >
                                            <td>{formatDate(t.created_at)}</td>
                                            <td>
                                                {/* B3: the type chip alone — the receipt-kind
                                                    jargon tags ('Booking Request', 'Card
                                                    verification', …) are gone from the wallet
                                                    lists. */}
                                                <span className={`cwallet__chip ${typeChipClass(t.type)}`}>
                                                    {typeLabel(t)}
                                                </span>
                                            </td>
                                            <td className="cwallet__descCell">
                                                {/* B2: descriptions wrap to AT MOST two lines
                                                    (full text on hover) so long rows can never
                                                    stretch the table into vertical scrolling. */}
                                                <div className="cwallet__descText" title={formatDescription(t)}>
                                                    {formatDescription(t)}
                                                </div>
                                                {invoiceIds.length > 0 && (
                                                    <span className="cwallet__refLinks">
                                                        {invoiceIds.map((inv, i) => {
                                                            // An invoice ref can be a scalar id OR a frozen-split
                                                            // object { invoice_id, invoice_number, amount } — a
                                                            // settlement transaction carries the object shape.
                                                            // Never render the object itself (React crash); pull a
                                                            // human label + a stable key out of it.
                                                            const isObj = inv && typeof inv === 'object';
                                                            const label = isObj
                                                                ? (inv.invoice_number || `#${inv.invoice_id}`)
                                                                : `#${inv}`;
                                                            const key = isObj
                                                                ? (inv.invoice_id ?? inv.invoice_number ?? i)
                                                                : inv;
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    className="cwallet__refLink"
                                                                    onClick={(e) => { e.stopPropagation(); navigate('/client/invoices'); }}
                                                                    title="Open your invoices"
                                                                >
                                                                    Invoice {label}
                                                                </button>
                                                            );
                                                        })}
                                                    </span>
                                                )}
                                            </td>
                                            <td className={parseFloat(t.amount) === 0 ? 'cwallet__amount--zero' : parseFloat(t.amount) > 0 ? 'cwallet__amount--positive' : 'cwallet__amount--negative'}>
                                                {parseFloat(t.amount) > 0 ? '+' : parseFloat(t.amount) < 0 ? '−' : ''}{money(Math.abs(parseFloat(t.amount)))}
                                            </td>
                                            <td className="cwallet__amountTotal">{money(parseFloat(t.cash_balance_after) + parseFloat(t.bonus_balance_after))}</td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="cwallet__pagination">
                                    <button
                                        className="cwallet__pageBtn"
                                        disabled={transactionsPage <= 1}
                                        onClick={() => fetchTransactions(transactionsPage - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="cwallet__pageInfo">
                                        Page {transactionsPage} of {totalPages}
                                    </span>
                                    <button
                                        className="cwallet__pageBtn"
                                        disabled={transactionsPage >= totalPages}
                                        onClick={() => fetchTransactions(transactionsPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Top-ups tab.
                P1 — this list is WALLET MONEY ONLY. Card payments made FOR A
                BOOKING are not top-ups and never appear here (they are receipts
                against the booking). The note says so out loud, because clients
                who paid by card at checkout used to come looking for the payment
                on this tab and conclude it had gone missing. */}
            {tab === 'deposits' && (
                <>
                    <div className="cwallet__tabNote">
                        Top-ups are money you add to your wallet. Card payments you made for a
                        specific booking aren’t top-ups — you’ll find those on the booking itself,
                        with their receipt.
                    </div>
                    {depositsLoading ? (
                        <div className="cwallet__loading"><LoadingPopup /></div>
                    ) : deposits.length === 0 ? (
                        <div className="cwallet__empty">
                            <div className="cwallet__empty-title">No Top-ups Yet</div>
                            <div className="cwallet__empty-text">Add funds to your wallet to get started.</div>
                        </div>
                    ) : (
                        <div className="cwallet__tableWrap">
                        <table className="cwallet__table">
                            <thead>
                                <tr>
                                    {/* B3 (owner): the Remaining Cash / Remaining Bonus
                                        columns are gone — they confused more than they
                                        informed. The wallet header already carries the
                                        one balance figure that matters. */}
                                    <th>Reference</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Bonus</th>
                                    <th>Total Credit</th>
                                    <th>Method</th>
                                    <th>Status</th>
                                    <th>Document</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.map((d) => (
                                    <tr key={d.id}>
                                        <td><span style={REF_CHIP}>TOP-{d.id}</span></td>
                                        <td>{formatDate(d.created_at)}</td>
                                        <td>{money(d.amount)}</td>
                                        <td>
                                            {parseFloat(d.bonus_amount) > 0
                                                ? `${money(d.bonus_amount)} (${d.bonus_percentage}%)`
                                                : '—'}
                                        </td>
                                        <td style={{ fontWeight: 900 }}>{money(d.total_credit)}</td>
                                        {/* B3: METHOD ONLY. Every row in this list is a
                                            genuine wallet top-up now (booking card payments
                                            are receipts on the booking), so the old
                                            'Booking Request' / 'Card verification' jargon
                                            chips said nothing a client needed — legacy rows
                                            render with the same plain method wording. */}
                                        <td>
                                            {d.payment_method === 'bank_transfer' ? 'Bank Transfer' : d.payment_method === 'card' ? 'Card' : 'Online'}
                                        </td>
                                        <td>
                                            {d.status === 'pending' && isReviewHeld(d) ? (
                                                <span className="cwallet__chip cwallet__chip--review" title={REVIEW_HELD_NOTE}>
                                                    Payment in review
                                                </span>
                                            ) : d.status === 'pending' && isAwaitingPayment(d) ? (
                                                <>
                                                    <span className="cwallet__chip cwallet__chip--awaitPay">Awaiting payment</span>
                                                    <div className="cwallet__awaitRowBtns">
                                                        <button
                                                            type="button"
                                                            className="cwallet__awaitBtn cwallet__awaitBtn--primary"
                                                            onClick={() => handleCompleteCheckout(d)}
                                                            disabled={!!checkoutActionId}
                                                        >
                                                            {checkoutAckTotals[d.id] != null ? 'Pay updated' : 'Complete'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="cwallet__awaitBtn cwallet__awaitBtn--ghost"
                                                            onClick={() => handleCancelCheckout(d)}
                                                            disabled={!!checkoutActionId}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                    {checkoutNotice && checkoutNotice.id === d.id && (
                                                        <div className="cwallet__awaitNotice">{checkoutNotice.text}</div>
                                                    )}
                                                </>
                                            ) : (
                                                <span className={`cwallet__chip ${depositStatusChip(d.status)}`}>
                                                    {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {d.status === 'approved' && d.zoho_payment_id ? (
                                                /* OPENS in a new tab (2026-08-14),
                                                   like the invoice PDFs elsewhere:
                                                   the endpoint serves it inline, so
                                                   a plain link is all this needs —
                                                   no blob, no scripted click, and
                                                   nothing to fail silently. */
                                                <a
                                                    className="cwallet__proofLink"
                                                    href={`/api/client-requests/invoices/payments/${d.zoho_payment_id}/pdf`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Receipt
                                                </a>
                                            ) : d.proof_of_payment_url ? (
                                                <a href={d.proof_of_payment_url} target="_blank" rel="noopener noreferrer" className="cwallet__proofLink">
                                                    Proof
                                                </a>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </>
            )}

            {/* Deposit form modal */}
            {showDepositForm && (
                <ClientDepositForm
                    walletClientId={walletId}
                    walletLabel={currentWallet && !currentWallet.is_main ? (currentWallet.entity_label || currentWallet.label) : null}
                    onClose={() => setShowDepositForm(false)}
                    onSuccess={handleDepositSuccess}
                />
            )}

            {/* Payout request modal */}
            {showPayoutModal && (
                <RequestPayoutModal
                    walletClientId={walletId}
                    withdrawable={withdrawable}
                    onClose={() => setShowPayoutModal(false)}
                    onSuccess={handlePayoutSuccess}
                />
            )}
        </div>
    );
}
